import { formatIstTime, type Campaign } from '@wa/shared';
import { CampaignStatusChip } from '@/campaigns/CampaignStatusChip';

/**
 * One broadcast, drawn the way the reference draws it: an outbound bubble holding
 * the template, and under it the eight lines of what happened.
 *
 * The lines and their arithmetic are the reference's own, checked against a real
 * broadcast of theirs (7,372 recipients): every percentage is of the recipient
 * total, "unread" is sent minus read, and "no response" is sent minus replied.
 * Not delivered-minus-read, which would be the more defensible number — the point
 * of this screen is that a client moving from DoubleTick reads the same figures.
 *
 * Each line is a drill-down into the recipient list filtered to that outcome,
 * which is the question every one of these numbers raises: *which* 473 failed.
 */
export type Drill = 'all' | 'delivered' | 'read' | 'failed' | 'replied' | 'unread' | 'noresponse';

export function BroadcastBubble({ campaign, onDrill }: { campaign: Campaign; onDrill: (c: Campaign, drill: Drill) => void }) {
  const total = campaign.totalRecipients;
  const sent = campaign.sentCount;
  const read = campaign.readCount;
  const replied = campaign.repliedCount ?? 0;
  const awaiting = campaign.templateStatus && campaign.templateStatus.toUpperCase() !== 'APPROVED'
    ? Math.max(total - sent - campaign.failedCount, 0)
    : 0;
  const notStarted = campaign.status === 0 || campaign.status === 1;

  const pct = (n: number) => (total > 0 ? `${((n / total) * 100).toFixed(1)}%` : '0.0%');

  const lines: { icon: React.ReactNode; n: number; label: string; drill: Drill; tone?: 'bad' | 'good' | 'info' }[] = [
    { icon: <Tick />, n: sent, label: 'Sent to', drill: 'all' },
    { icon: <Tick double />, n: campaign.deliveredCount, label: 'Delivered to', drill: 'delivered' },
    { icon: <IconUnread />, n: Math.max(sent - read, 0), label: 'Unread by', drill: 'unread' },
    { icon: <Tick double blue />, n: read, label: 'Read by', drill: 'read', tone: 'info' },
    { icon: <IconReply />, n: replied, label: 'Replied by', drill: 'replied' },
    { icon: <IconNoReply />, n: Math.max(sent - replied, 0), label: 'No response by', drill: 'noresponse' },
    { icon: <IconClock />, n: awaiting, label: 'Awaiting template approval', drill: 'all' },
    { icon: <IconFailed />, n: campaign.failedCount, label: 'Failed', drill: 'failed', tone: 'bad' },
  ];

  const stamp = campaign.startedAt ?? campaign.scheduledAt ?? campaign.createdAt;

  return (
    <div className="flex justify-end">
      <article className="w-full max-w-[min(30rem,92%)] overflow-hidden rounded-lg bg-(--color-bubble-out) text-(--color-bubble-ink) shadow-sm">
        {/* A draft is a send somebody started and left. The whole bubble reopens
            it: a draft that can only be seen is a draft that gets recreated. */}
        <div
          className={`px-3 pt-2.5 pb-1.5 ${campaign.status === 0 ? 'cursor-pointer hover:bg-(--color-brand)/8' : ''}`}
          onClick={campaign.status === 0 ? () => onDrill(campaign, 'all') : undefined}
          role={campaign.status === 0 ? 'button' : undefined}
          tabIndex={campaign.status === 0 ? 0 : undefined}
          onKeyDown={campaign.status === 0 ? (e) => { if (e.key === 'Enter' || e.key === ' ') onDrill(campaign, 'all'); } : undefined}
        >
          <p className="mb-1 text-micro font-semibold text-(--color-bubble-sender)">{campaign.templateName}</p>
          <p className="text-body whitespace-pre-wrap">{campaign.templateBody ?? campaign.name}</p>
          <div className="mt-1.5 flex items-center justify-end gap-2">
            {notStarted && <CampaignStatusChip campaign={campaign} />}
            <span className="text-micro text-(--color-bubble-stamp) tabular-nums">{formatIstTime(stamp)}</span>
          </div>
        </div>

        {/* The report. Hidden on a broadcast that has not gone yet: eight zeros
            under a scheduled message read as eight failures. */}
        {!notStarted && (
          <ul className="border-t border-(--color-brand)/15">
            {lines.map((l, i) => (
              (l.label !== 'Awaiting template approval' || awaiting > 0) && (
                <li key={l.label} className={i > 0 ? 'border-t border-(--color-brand)/10' : ''}>
                  <button
                    type="button"
                    onClick={() => onDrill(campaign, l.drill)}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-body hover:bg-(--color-brand)/8"
                  >
                    <span className={[
                      'shrink-0',
                      l.tone === 'bad' ? 'text-(--color-bad)' : l.tone === 'info' ? 'text-(--color-tick-read)' : 'text-(--color-ink-soft)',
                    ].join(' ')}>{l.icon}</span>
                    <span className="min-w-0 flex-1 truncate">
                      <span className="tabular-nums">{l.n.toLocaleString('en-IN')}</span>
                      <span className="text-(--color-muted)"> - </span>{l.label}
                      <span className="ml-2 font-medium tabular-nums">{pct(l.n)}</span>
                    </span>
                    {l.drill === 'failed' && l.n > 0 && (
                      <span className="shrink-0 text-small font-medium text-(--color-bad)">View reasons</span>
                    )}
                    <span aria-hidden="true" className="shrink-0 text-(--color-muted)"><IconArrow /></span>
                  </button>
                </li>
              )
            ))}
          </ul>
        )}
      </article>
    </div>
  );
}

const s = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
function Tick({ double = false, blue = false }: { double?: boolean; blue?: boolean }) {
  return (
    <svg viewBox="0 0 20 20" className={`size-4 ${blue ? 'text-(--color-tick-read)' : ''}`} aria-hidden="true">
      <path d="m3 10.5 3.2 3.2L12 7.5" {...s} />
      {double && <path d="m9 13.7 1 1L16.5 8" {...s} />}
    </svg>
  );
}
const IconUnread = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><rect x="3" y="5" width="14" height="10" rx="1.5" {...s} /><path d="m3 6.5 7 4.5 7-4.5" {...s} /></svg>;
const IconReply = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="M8 5 3.5 9.5 8 14M4 9.5h7.5a4 4 0 0 1 4 4V15" {...s} /></svg>;
const IconNoReply = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="M8 5 3.5 9.5 8 14M4 9.5h7.5a4 4 0 0 1 4 4V15" {...s} /><path d="m14 4 3 3M17 4l-3 3" {...s} /></svg>;
const IconClock = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><circle cx="10" cy="10" r="6.5" {...s} /><path d="M10 6.5V10l2.4 1.5" {...s} /></svg>;
const IconFailed = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><circle cx="10" cy="10" r="6.5" {...s} /><path d="m7.5 7.5 5 5M12.5 7.5l-5 5" {...s} /></svg>;
const IconArrow = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="M4 10h11M11 6l4 4-4 4" {...s} /></svg>;
